import React, { FC, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  Work,
  TrendingUp,
  Star,
  Assignment,
} from '@mui/icons-material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { StaffPerformanceViewProps } from '../performance.interfaces';
import { usePerformance } from '../performance.context';
import {
  getRatingColor,
  getStatusColor,
  getRatingLabel,
  getStatusLabel,
  formatScore,
  formatDate,
} from '../performance.helpers';
import { useTranslation } from 'src/hooks/useTranslationHook';
import PaginatedGrid from 'src/views/components/paginatedGrid/PaginatedGrid';

const PerformanceStaffView: FC<StaffPerformanceViewProps> = ({
  reviews,
  summary,
  onReviewSelect,
}) => {
  const { fetchStaffReviews, state } = usePerformance();
  const { translateText } = useTranslation();

  useEffect(() => {
    if (reviews.length === 0) {
      fetchStaffReviews();
    }
  }, [reviews.length, fetchStaffReviews]);

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'staffMember',
      headerName: translateText(
        'analytics.performance.staff.columns.staffMember'
      ),
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
            {params.row.name.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translateText('analytics.performance.staff.id')}:{' '}
              {params.row.employeeId}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'department',
      headerName: translateText(
        'analytics.performance.staff.columns.department'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{ textTransform: 'capitalize' }}
        >
          {params.row.department.replace('_', ' ')}
        </Typography>
      ),
    },
    {
      field: 'position',
      headerName: translateText(
        'analytics.performance.staff.columns.position'
      ),
      width: 150,
    },
    {
      field: 'overallScore',
      headerName: translateText(
        'analytics.performance.staff.columns.overallScore'
      ),
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight="medium">
          {formatScore(params.row.overallScore)}
        </Typography>
      ),
    },
    {
      field: 'rating',
      headerName: translateText(
        'analytics.performance.staff.columns.rating'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getRatingLabel(
            params.row.overallRating,
            translateText
          )}
          size="small"
          sx={{
            backgroundColor: getRatingColor(params.row.overallRating),
            color: 'white',
            fontWeight: 'medium',
          }}
        />
      ),
    },
    {
      field: 'status',
      headerName: translateText(
        'analytics.performance.staff.columns.status'
      ),
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getStatusLabel(params.row.status, translateText)}
          size="small"
          variant="outlined"
          sx={{
            borderColor: getStatusColor(params.row.status),
            color: getStatusColor(params.row.status),
          }}
        />
      ),
    },
    {
      field: 'recommendations',
      headerName: translateText(
        'analytics.performance.staff.columns.recommendations'
      ),
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {params.row.promotionRecommendation && (
            <Tooltip
              title={translateText(
                'analytics.performance.staff.promotionRecommended'
              )}
            >
              <Chip
                label={translateText(
                  'analytics.performance.staff.promotion'
                )}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            </Tooltip>
          )}
          {params.row.salaryReviewRecommendation && (
            <Tooltip
              title={translateText(
                'analytics.performance.staff.salaryReviewRecommended'
              )}
            >
              <Chip
                label={translateText(
                  'analytics.performance.staff.salary'
                )}
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'reviewDate',
      headerName: translateText(
        'analytics.performance.staff.columns.reviewDate'
      ),
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatDate(params.row.reviewDate)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: translateText(
        'analytics.performance.staff.columns.actions'
      ),
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip
          title={translateText(
            'analytics.performance.staff.viewDetails'
          )}
        >
          <IconButton
            size="small"
            onClick={() => onReviewSelect(params.row)}
            color="primary"
            sx={{
              minHeight: { xs: '44px', sm: 'auto' },
              minWidth: { xs: '44px', sm: 'auto' },
            }}
          >
            <Visibility />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [translateText, onReviewSelect]);

  if (state.isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {translateText('analytics.performance.staff.loading')}
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  const promotionRecommended = reviews.filter(
    (r) => r.promotionRecommendation
  ).length;
  const salaryReviewRecommended = reviews.filter(
    (r) => r.salaryReviewRecommendation
  ).length;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }} data-testid="performance-staff-view">
      {/* Summary Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 4 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Work color="primary" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.staff.totalReviews')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {summary.totalReviews}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText(
                  'analytics.performance.staff.staffPerformanceReviews'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Star color="success" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="success.main"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.staff.avgRating')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {summary.averageRating.toFixed(1)}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText('analytics.performance.staff.outOf10')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp color="success" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="success.main"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.staff.promotions')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {promotionRecommended}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText(
                  'analytics.performance.staff.recommendedForPromotion'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Assignment color="info" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                <Typography
                  variant="h6"
                  color="info.main"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('analytics.performance.staff.salaryReview')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {salaryReviewRecommended}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText(
                  'analytics.performance.staff.recommendedForReview'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Distribution Chart */}
      <Card elevation={2} sx={{ mb: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {translateText(
              'analytics.performance.staff.performanceDistribution'
            )}
          </Typography>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {Object.entries(summary.ratingDistribution).map(
              ([rating, count]) => (
                <Grid item xs={6} sm={4} md={2.4} key={rating}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      sx={{
                        color: getRatingColor(rating as any),
                        fontSize: { xs: '1.5rem', sm: '2.125rem' },
                      }}
                    >
                      {count}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        textTransform: 'capitalize',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      {rating.replace(/([A-Z])/g, ' $1').trim()}
                    </Typography>
                  </Box>
                </Grid>
              )
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Staff Reviews Table */}
      <Card elevation={2}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {translateText(
              'analytics.performance.staff.staffPerformanceReviews'
            )}
          </Typography>
          {reviews.length === 0 && !state.isLoading ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
              <Work sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                {translateText('analytics.performance.staff.noReviewsFound')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {translateText('analytics.performance.staff.reviewsWillAppear')}
              </Typography>
            </Box>
          ) : (
            <PaginatedGrid
              dataTestId="performance-staff-grid"
              columns={columns}
              rows={reviews}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PerformanceStaffView;
